import React, { useState, useMemo, useEffect } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import filterMenu from 'assets/img/filter-menu.svg';
import userIcon from 'assets/img/avatar1.png';
import { findLetter } from '../utils/general';
import { useAuth } from 'contexts/AuthContext';

const FilterDropdown = ({
  milestones = [],
  members = [],
  priorityOptions = [],
  dueDateOptions = [],
  setFilteredMilestones,
  isMasterCalender,
  boardLabels = [],
  reloadMilestones,
  projectBoardId,
  calendarEvents,
  setFilteredCalendarEvents,
  setshowArchivedCards
}) => {
  const [filters, setFilters] = useState({
    assigneeNames: [],
    priority: [],
    dueDateFilter: [],
    labels: [],
    noMember: false,
    assignedToMe: false,
    cardStatus: []
  });
  const [showArchived, setShowArchived] = useState(false);
  // console.log(boardLabels, 'boardLabels');
  const { user } = useAuth();
  const handleCheckboxChange = (section, value) => {
    if (section === 'noMember' || section === 'assignedToMe') {
      setFilters((prev) => ({ ...prev, [section]: !prev[section] }));
    } else {
      setFilters((prev) => {
        const updated = prev[section].includes(value)
          ? prev[section].filter((item) => item !== value)
          : [...prev[section], value];
        return { ...prev, [section]: updated };
      });
    }
  };

  const handleShowArchivedChange = (e) => {
    if (isMasterCalender) return;

    console.log(e.target.checked);

    setshowArchivedCards(e?.target?.checked);
    setShowArchived((prev) => {
      const next = !prev;
      reloadMilestones(projectBoardId, next);
      return next;
    });
  };

  // --- date helpers (put above matchesDueDate) ---
  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };

  // weekStartsOn = 1 -> Monday (change to 0 if your week starts Sunday)
  const startOfWeek = (d, weekStartsOn = 1) => {
    const x = startOfDay(d);
    const day = x.getDay(); // 0 Sun … 6 Sat
    const diff = (day - weekStartsOn + 7) % 7;
    x.setDate(x.getDate() - diff);
    return x;
  };

  // --- replace your matchesDueDate with this ---
  const matchesDueDate = (dueDate) => {
    const rawToday = new Date();
    const today = startOfDay(rawToday);
    const date = dueDate ? startOfDay(new Date(dueDate)) : null;

    if (!date) {
      return filters.dueDateFilter.includes('No Due Date');
    }

    // Overdue: strictly before today (calendar)
    const isOverdue = date < today;

    // Next Day: tomorrow only (calendar day)
    const tomorrowStart = addDays(today, 1);
    const dayAfterTomorrowStart = addDays(today, 2);
    const isNextDay = date >= tomorrowStart && date < dayAfterTomorrowStart;

    // Next Week: the *next* calendar week (Mon..Sun if weekStartsOn=1)
    const currentWeekStart = startOfWeek(today, 1);
    const nextWeekStart = addDays(currentWeekStart, 7);
    const weekAfterNextStart = addDays(currentWeekStart, 14);
    const isNextWeek = date >= nextWeekStart && date < weekAfterNextStart;

    // Next Month: full next calendar month
    const firstDayNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const firstDayMonthAfter = new Date(today.getFullYear(), today.getMonth() + 2, 1);
    const isNextMonth = date >= firstDayNextMonth && date < firstDayMonthAfter;

    return (
      (filters.dueDateFilter.includes('Overdue') && isOverdue) ||
      (filters.dueDateFilter.includes('Due in Next Day') && isNextDay) ||
      (filters.dueDateFilter.includes('Due in Next Week') && isNextWeek) ||
      (filters.dueDateFilter.includes('Due in Next Month') && isNextMonth)
    );
  };

  const matchesLabels = (labels = []) => {
    if (filters.labels.length === 0) return false;
    return labels.some((l) => filters.labels.includes(l._id));
  };

  const matchesCardStatus = (isCompleted) =>
    filters.cardStatus.length > 0 &&
    ((filters.cardStatus.includes('completed') && isCompleted === true) ||
      (filters.cardStatus.includes('not_completed') && isCompleted !== true));

  const getAssigneeId = (assignee) => (typeof assignee === 'string' ? assignee : assignee?._id);

  const matchesCurrentUser = (assignees = []) =>
    filters.assignedToMe && assignees.some((assignee) => getAssigneeId(assignee) === user?._id);

  const matchesSelectedMember = (assignees = []) => {
    if (filters.assigneeNames.length === 0) return false;

    const selectedMemberIds = members
      .filter((member) => filters.assigneeNames.includes(member.fullName))
      .map((member) => member._id);

    return assignees.some((assignee) => selectedMemberIds.includes(getAssigneeId(assignee)));
  };

  // Active filter categories are combined with AND; options inside one category stay OR.
  // `skipStatus` is for items that have no completion status (checklists, subtasks).
  const matchesAllFilters = ({ member, priority, due, label, status }, skipStatus = false) => {
    const hasMember = filters.noMember || filters.assignedToMe || filters.assigneeNames.length > 0;
    const hasPriority = filters.priority.length > 0;
    const hasDue = filters.dueDateFilter.length > 0;
    const hasLabel = filters.labels.length > 0;
    const hasStatus = filters.cardStatus.length > 0;
    if (skipStatus && hasStatus && !hasMember && !hasPriority && !hasDue && !hasLabel) return false;
    return (
      (!hasMember || !!member) &&
      (!hasPriority || !!priority) &&
      (!hasDue || !!due) &&
      (!hasLabel || !!label) &&
      (skipStatus || !hasStatus || !!status)
    );
  };

  const filteredEvents = useMemo(() => {
    const noFiltersApplied =
      filters.assigneeNames.length === 0 &&
      filters.priority.length === 0 &&
      filters.dueDateFilter.length === 0 &&
      filters.labels.length === 0 &&
      filters.cardStatus.length === 0 &&
      !filters.noMember &&
      !filters.assignedToMe;

    const base = noFiltersApplied
      ? calendarEvents
      : calendarEvents.filter((ev) => {
          const hasNoMembers = !ev.assigneeIds || ev.assigneeIds.length === 0;

          const assignedToMe = matchesCurrentUser(ev.assigneeIds);

          const memberSelected = matchesSelectedMember(ev.assigneeIds);

          const noMemberMatch = filters.noMember && hasNoMembers;

          const dueDateMatch = filters.dueDateFilter.length && matchesDueDate(ev.start);

          const statusMatch = matchesCardStatus(ev.isCompleted);

          const priorityMatch = filters.priority.length && ev.priorityId && filters.priority.includes(ev.priorityId);

          const labelMatch = filters.labels.length && ev.labelIds?.some((id) => filters.labels.includes(id));

          return matchesAllFilters({
            member: noMemberMatch || assignedToMe || memberSelected,
            priority: priorityMatch,
            due: dueDateMatch,
            label: labelMatch,
            status: statusMatch
          });
        });

    // ✅ SORT BY DUE DATE (earliest first, nulls last)
    return [...base].sort((a, b) => {
      const da = a.start ? new Date(a.start).getTime() : Infinity;
      const db = b.start ? new Date(b.start).getTime() : Infinity;
      return da - db;
    });
  }, [filters, calendarEvents, members]);

  useEffect(() => {
    setFilteredCalendarEvents(filteredEvents);
  }, [filteredEvents]);

  const filteredMilestones = useMemo(() => {
    const noFiltersApplied =
      filters.assigneeNames.length === 0 &&
      filters.priority.length === 0 &&
      filters.dueDateFilter.length === 0 &&
      filters.labels.length === 0 &&
      filters.cardStatus.length === 0 &&
      !filters.noMember &&
      !filters.assignedToMe;

    if (noFiltersApplied) return milestones;

    return milestones
      .map((ms) => {
        /* ---------- FILTER CHECKLISTS ---------- */
        const filteredChecklists = (ms.checklist || []).filter((ch) => {
          const hasNoMembers = !ch.assignees || ch.assignees.length === 0;

          const assignedToMe = matchesCurrentUser(ch.assignees);

          const memberSelected = matchesSelectedMember(ch.assignees);

          const prioritySelected = filters.priority.length && ch.priority && filters.priority.includes(ch.priority._id);

          const dueDateSelected = filters.dueDateFilter.length && matchesDueDate(ch.dueDate);

          const labelSelected = filters.labels.length && matchesLabels(ch.labelIds);

          const noMemberSelected = filters.noMember && hasNoMembers;

          return matchesAllFilters(
            {
              member: noMemberSelected || memberSelected || assignedToMe,
              priority: prioritySelected,
              due: dueDateSelected,
              label: labelSelected
            },
            true
          );
        });

        /* ---------- FILTER SUBTASKS ---------- */
        const filteredSubtasks = (ms.subtask || [])
          .map((st) => {
            const filteredSubtaskChecklists = (st.checklist || []).filter((ch) => {
              const hasNoMembers = !ch.assignees || ch.assignees.length === 0;

              const assignedToMe = matchesCurrentUser(ch.assignees);

              const memberSelected = matchesSelectedMember(ch.assignees);

              const prioritySelected =
                filters.priority.length && ch.priority && filters.priority.includes(ch.priority._id);

              const dueDateSelected = filters.dueDateFilter.length && matchesDueDate(ch.dueDate);

              const labelSelected = filters.labels.length && matchesLabels(ch.labelIds);

              const noMemberSelected = filters.noMember && hasNoMembers;

              return matchesAllFilters(
                {
                  member: noMemberSelected || memberSelected || assignedToMe,
                  priority: prioritySelected,
                  due: dueDateSelected,
                  label: labelSelected
                },
                true
              );
            });

            const subtaskMatches =
              filteredSubtaskChecklists.length > 0 ||
              matchesAllFilters(
                {
                  member:
                    (filters.noMember && (!st.assignees || st.assignees.length === 0)) ||
                    matchesSelectedMember(st.assignees) ||
                    matchesCurrentUser(st.assignees),
                  priority: filters.priority.length && st.priority && filters.priority.includes(st.priority._id),
                  due: filters.dueDateFilter.length && matchesDueDate(st.dueDate),
                  label: filters.labels.length && matchesLabels(st.labelIds)
                },
                true
              );

            if (!subtaskMatches) return null;

            return {
              ...st,
              checklist: filteredSubtaskChecklists
            };
          })
          .filter(Boolean);

        /* ---------- MILESTONE MATCH ---------- */
        const hasNoMembers = !ms.assigneeIds || ms.assigneeIds.length === 0;

        const milestoneMatches = matchesAllFilters({
          member: (filters.noMember && hasNoMembers) || matchesSelectedMember(ms.assigneeIds) || matchesCurrentUser(ms.assigneeIds),
          priority: filters.priority.length && ms.priority && filters.priority.includes(ms.priority._id),
          due: filters.dueDateFilter.length && matchesDueDate(ms.kbDueDate ?? ms.dueDate),
          label: filters.labels.length && matchesLabels(ms.labelIds),
          status: matchesCardStatus(ms.isCompleted)
        });

        /* ---------- KEEP IF ANY CHILD OR SELF MATCHES ---------- */
        if (milestoneMatches || filteredChecklists.length > 0 || filteredSubtasks.length > 0) {
          return {
            ...ms,
            checklist: filteredChecklists,
            subtask: filteredSubtasks
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [filters, milestones, members]);

  // ✅ Always update parent when filters change
  useEffect(() => {
    // setMilestones(filteredMilestones);
    setFilteredMilestones(filteredMilestones);
  }, [filteredMilestones]);

  return (
    <Dropdown className="filter-cover">
      <Dropdown.Toggle variant="outline-primary" id="filterDropdown" className="filter-menu">
        <img src={filterMenu} alt="filterMenu" className="filterMenu" />
      </Dropdown.Toggle>

      <Dropdown.Menu className="p-3" style={{ width: '250px' }}>
        <div className="filter-content">
          <div className="panel_head">Filter</div>
          <div className="panel_body">
            {/* Members Section */}

            {!isMasterCalender && (
              <div className="sec-content">
                <div className="sec_title">Members</div>

                <label className="checkbox_cover">
                  <input type="checkbox" checked={filters.noMember} onChange={() => handleCheckboxChange('noMember')} />
                  <span className="chk_box"></span>
                  <span className="label">No members</span>
                </label>

                <label className="checkbox_cover">
                  <input
                    type="checkbox"
                    checked={filters.assignedToMe}
                    onChange={() => handleCheckboxChange('assignedToMe')}
                  />
                  <span className="chk_box"></span>

                  <span className="avatar avatar-amber" aria-hidden="true">
                    <img
                      style={{ borderRadius: '50%' }}
                      src={user?.profilePic || userIcon}
                      alt={user?.fullName}
                      className="userIcon"
                    />
                  </span>

                  <span className="label">Me</span>
                </label>

                {members.map((member) => (
                  <label key={member._id} className="checkbox_cover">
                    <input
                      type="checkbox"
                      checked={filters.assigneeNames.includes(member.fullName)}
                      onChange={() => handleCheckboxChange('assigneeNames', member.fullName)}
                    />
                    <span className="chk_box"></span>
                    <span className="avatar avatar-amber" aria-hidden="true">
                      {member?.profilePic ? (
                        <img
                          style={{ borderRadius: '50%' }}
                          src={member?.profilePic || userIcon}
                          alt={member.fullName}
                          className="user"
                        />
                      ) : (
                        findLetter(member.fullName)
                      )}
                    </span>
                    <span className="label">{member.fullName}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="sec-content">
              <div className="sec_title">Card Status</div>

              <label className="checkbox_cover">
                <input
                  type="checkbox"
                  checked={filters.cardStatus.includes('completed')}
                  onChange={() => handleCheckboxChange('cardStatus', 'completed')}
                />
                <span className="chk_box"></span>
                <span className="label">Marked as complete</span>
              </label>

              <label className="checkbox_cover">
                <input
                  type="checkbox"
                  checked={filters.cardStatus.includes('not_completed')}
                  onChange={() => handleCheckboxChange('cardStatus', 'not_completed')}
                />
                <span className="chk_box"></span>
                <span className="label">Not marked as complete</span>
              </label>
            </div>
            {/* Due Date Section */}
            {!isMasterCalender && (
              <div className="sec-content">
                <div className="sec_title">Due date</div>
                {dueDateOptions.map((opt) => (
                  <label key={opt.label} className="checkbox_cover">
                    <input
                      type="checkbox"
                      checked={filters.dueDateFilter.includes(opt.label)}
                      onChange={() => handleCheckboxChange('dueDateFilter', opt.label)}
                    />
                    <span className="chk_box"></span>
                    <span className="label">{opt.label}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Priority Section */}
            <div className="sec-content">
              <div className="sec_title">Priority</div>
              {priorityOptions.map((priority) => (
                <label key={priority.value} className="checkbox_cover">
                  <input
                    type="checkbox"
                    checked={filters.priority.includes(priority.value)}
                    onChange={() => handleCheckboxChange('priority', priority.value)}
                  />
                  <span className="chk_box"></span>
                  <span className="label">{priority.label}</span>
                </label>
              ))}
            </div>
            {/* Labels Section */}
            {boardLabels.length > 0 && (
              <div className="sec-content">
                <div className="sec_title">Labels</div>

                {boardLabels.map((label) => (
                  <label key={label._id} className="checkbox_cover">
                    <input
                      type="checkbox"
                      checked={filters.labels.includes(label._id)}
                      onChange={() => handleCheckboxChange('labels', label._id)}
                    />
                    <span className="chk_box"></span>

                    <span
                      className="label-dot"
                      style={{
                        backgroundColor: label.color,
                        color: label.textColor || '#000'
                      }}
                    />

                    <span className="label">{label.name}</span>
                  </label>
                ))}
              </div>
            )}
            {!isMasterCalender && (
              <div className="sec-content">
                <div className="sec_title">Milestones</div>
                <label className="checkbox_cover">
                  <input type="checkbox" checked={showArchived} onChange={handleShowArchivedChange} />
                  <span className="chk_box"></span>
                  <span className="label">Show archived</span>
                </label>
              </div>
            )}
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default FilterDropdown;
